import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    GroupsRounded,
    PersonRounded,
    Public,
    SportsEsports,
    WarningAmberOutlined,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { colors } from "../../theme/colors";
import { getMapImageUrl, getMapMetaById } from "../../constants/mapMeta";
import {
    MAP_VOTE_ACCEPT_HEADLINE,
    MAP_VOTE_ACCEPTED_HEADLINE,
    MAP_VOTE_ACCEPT_HEADLINE_SUFFIX,
    MAP_VOTE_ALL_READY_LABEL,
    MAP_VOTE_PREPARING_LABEL,
} from "../../constants/mapVote";
import utils from "../../universal/utils";
import {
    pickAvatarUpdatedAtMs,
    resolveAvatarSrc,
} from "../../utils/avatarUrl";
import { MOBILE_NAV_MQ, SHORT_VIEWPORT_MQ } from "./lobbyPageStyles";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";
import LobbyMainCta from "./LobbyMainCta";
import {
    buildViewerPlayerSlots,
    formatAvgEloValue,
    formatMatchTeamName,
    remainingSecondsFromExpiresAt,
} from "../../utils/matchAcceptance";

const MotionBox = motion(Box);

const REPUTATION_WARNING =
    "Declining repeatedly may affect your reputation.";

const RING_SIZE = 52;
const RING_RADIUS = 21;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const MONO = "'JetBrains Mono', monospace";
const DISPLAY = "'Orbitron', sans-serif";

function avgEloFromSlots(slots) {
    const elos = (Array.isArray(slots) ? slots : [])
        .map((slot) => Number(slot?.member?.elo))
        .filter((elo) => Number.isFinite(elo));
    if (elos.length === 0) return null;
    return Math.round(elos.reduce((sum, elo) => sum + elo, 0) / elos.length);
}

function slotDisplayName(slot) {
    if (slot?.kind === "self" || slot?.kind === "party") {
        const name =
            slot?.member?.nickname ||
            slot?.member?.name ||
            slot?.member?.username ||
            "Player";
        return slot?.kind === "self" ? `${name} (you)` : name;
    }
    return slot?.accepted ? "Ready" : "Waiting";
}

function WaitingLabel({ accepted }) {
    if (accepted) {
        return "Ready";
    }

    return (
        <Box
            component="span"
            sx={{
                display: "inline-flex",
                alignItems: "baseline",
            }}
        >
            Waiting
            <Box
                component="span"
                aria-hidden
                sx={{
                    display: "inline-flex",
                    width: "1.2em",
                    letterSpacing: "0.02em",
                    "@keyframes matchReadyDot1": {
                        "0%, 8%": { opacity: 0 },
                        "20%, 68%": { opacity: 1 },
                        "82%, 100%": { opacity: 0 },
                    },
                    "@keyframes matchReadyDot2": {
                        "0%, 24%": { opacity: 0 },
                        "36%, 68%": { opacity: 1 },
                        "82%, 100%": { opacity: 0 },
                    },
                    "@keyframes matchReadyDot3": {
                        "0%, 40%": { opacity: 0 },
                        "52%, 68%": { opacity: 1 },
                        "82%, 100%": { opacity: 0 },
                    },
                    "& > span": {
                        display: "inline-block",
                        animationDuration: "2.8s",
                        animationTimingFunction: "ease-in-out",
                        animationIterationCount: "infinite",
                    },
                    "& > span:nth-of-type(1)": {
                        animationName: "matchReadyDot1",
                    },
                    "& > span:nth-of-type(2)": {
                        animationName: "matchReadyDot2",
                    },
                    "& > span:nth-of-type(3)": {
                        animationName: "matchReadyDot3",
                    },
                    "@media (prefers-reduced-motion: reduce)": {
                        "& > span": {
                            animation: "none",
                            opacity: 1,
                        },
                    },
                }}
            >
                <Box component="span">.</Box>
                <Box component="span">.</Box>
                <Box component="span">.</Box>
            </Box>
        </Box>
    );
}

WaitingLabel.propTypes = {
    accepted: PropTypes.bool,
};

function slotAriaLabel(slot) {
    const accepted = Boolean(slot?.accepted);
    if (slot?.kind === "self") {
        return accepted ? "You accepted" : "You — pending";
    }
    if (slot?.kind === "party") {
        const name = slotDisplayName(slot);
        return accepted ? `${name} accepted` : `${name} pending`;
    }
    return accepted ? "Opponent accepted" : "Opponent pending";
}

function CircularCountdown({ timeLeft, progress, isUrgent }) {
    const rawId = useId();
    const gradId = `matchReadyRing-${rawId.replace(/:/g, "")}`;
    const clamped = Math.min(1, Math.max(0, progress));
    const offset = RING_CIRCUMFERENCE * (1 - clamped);
    const accent = isUrgent ? colors.state.danger.main : colors.primary.main;
    const seconds = Math.max(0, timeLeft);

    return (
        <Box
            aria-label={`${seconds} seconds left to accept`}
            sx={{ flexShrink: 0 }}
        >
            <Box
                sx={{
                    position: "relative",
                    width: { xs: "3.55rem", xl: "4.15rem" },
                    height: { xs: "3.55rem", xl: "4.15rem" },
                    [MOBILE_NAV_MQ]: {
                        width: "3.05rem",
                        height: "3.05rem",
                    },
                    [SHORT_VIEWPORT_MQ]: {
                        width: "2.75rem",
                        height: "2.75rem",
                    },
                }}
            >
                <Box
                    component="svg"
                    viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
                    sx={{
                        width: "100%",
                        height: "100%",
                        transform: "rotate(-90deg)",
                        display: "block",
                    }}
                >
                    <defs>
                        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor={colors.accent.main} />
                            <stop offset="100%" stopColor={colors.primary.main} />
                        </linearGradient>
                    </defs>
                    <circle
                        cx={RING_SIZE / 2}
                        cy={RING_SIZE / 2}
                        r={RING_RADIUS}
                        fill="none"
                        stroke={alpha(colors.primary.main, 0.16)}
                        strokeWidth="4"
                    />
                    <circle
                        cx={RING_SIZE / 2}
                        cy={RING_SIZE / 2}
                        r={RING_RADIUS}
                        fill="none"
                        stroke={isUrgent ? accent : `url(#${gradId})`}
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeDasharray={RING_CIRCUMFERENCE}
                        strokeDashoffset={offset}
                        style={{ transition: "stroke-dashoffset 0.08s linear" }}
                    />
                </Box>
                <Typography
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "grid",
                        placeItems: "center",
                        fontFamily: MONO,
                        fontSize: { xs: "0.92rem", xl: "1.08rem" },
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                        color: isUrgent
                            ? colors.state.danger.main
                            : colors.text.primary,
                        lineHeight: 1,
                        [MOBILE_NAV_MQ]: {
                            fontSize: "0.82rem",
                        },
                    }}
                >
                    {seconds}s
                </Typography>
            </Box>
        </Box>
    );
}

CircularCountdown.propTypes = {
    timeLeft: PropTypes.number.isRequired,
    progress: PropTypes.number.isRequired,
    isUrgent: PropTypes.bool,
};

function MatchMetaItem({ icon: Icon, label, value }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.55rem",
                minWidth: 0,
            }}
        >
            <Box
                aria-hidden
                sx={{
                    width: { xs: "1.7rem", xl: "1.85rem" },
                    height: { xs: "1.7rem", xl: "1.85rem" },
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    border: `1px solid ${alpha("#fff", 0.12)}`,
                    background: alpha("#000", 0.35),
                    color: alpha("#fff", 0.78),
                    [MOBILE_NAV_MQ]: {
                        width: "1.45rem",
                        height: "1.45rem",
                    },
                }}
            >
                <Icon
                    sx={{
                        fontSize: { xs: "0.82rem", xl: "0.88rem" },
                        [MOBILE_NAV_MQ]: {
                            fontSize: "0.72rem",
                        },
                    }}
                />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: { xs: "0.52rem", xl: "0.56rem" },
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: alpha("#fff", 0.55),
                        lineHeight: 1.1,
                        [MOBILE_NAV_MQ]: {
                            fontSize: "0.46rem",
                        },
                    }}
                >
                    {label}
                </Typography>
                <Typography
                    sx={{
                        mt: "0.12rem",
                        fontSize: { xs: "0.86rem", xl: "0.94rem" },
                        fontWeight: 700,
                        color: colors.text.primary,
                        lineHeight: 1.1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        [MOBILE_NAV_MQ]: {
                            fontSize: "0.74rem",
                        },
                    }}
                >
                    {value}
                </Typography>
            </Box>
        </Box>
    );
}

MatchMetaItem.propTypes = {
    icon: PropTypes.elementType.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
};

function MatchMetaRow({ modeLabel, typeLabel, regionLabel }) {
    const items = [
        { key: "mode", icon: SportsEsports, label: "Mode", value: modeLabel },
        { key: "type", icon: GroupsRounded, label: "Type", value: typeLabel },
        { key: "region", icon: Public, label: "Region", value: regionLabel },
    ];

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                textAlign: "left",
                flexWrap: "wrap",
                gap: { xs: "0.55rem 0.85rem", xl: "0.65rem 1.15rem" },
                [MOBILE_NAV_MQ]: {
                    gap: "0.45rem 0.7rem",
                },
            }}
        >
            {items.map((item, index) => (
                <Box
                    key={item.key}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: "0.55rem", xl: "0.85rem" },
                    }}
                >
                    {index > 0 ? (
                        <Box
                            aria-hidden
                            sx={{
                                width: "1px",
                                height: "1.35rem",
                                background: alpha("#fff", 0.16),
                                flexShrink: 0,
                                [MOBILE_NAV_MQ]: {
                                    display: "none",
                                },
                            }}
                        />
                    ) : null}
                    <MatchMetaItem
                        icon={item.icon}
                        label={item.label}
                        value={item.value}
                    />
                </Box>
            ))}
        </Box>
    );
}

MatchMetaRow.propTypes = {
    modeLabel: PropTypes.string.isRequired,
    typeLabel: PropTypes.string.isRequired,
    regionLabel: PropTypes.string.isRequired,
};

function PlayerAvatar({ slot, avatarCacheBust, edgeBasePath, side }) {
    const member = slot?.member;
    const isKnown = slot?.kind === "self" || slot?.kind === "party";
    const accepted = Boolean(slot?.accepted);
    const avatarVersion = pickAvatarUpdatedAtMs(member, avatarCacheBust);
    const avatarSrc = isKnown
        ? resolveAvatarSrc({
              basePath: edgeBasePath,
              picture: member?.picture,
              userId: member?.userId,
              avatarUpdatedAtMs: avatarVersion,
              hasCustomAvatar: Boolean(member?.avatar),
          })
        : null;
    const [displaySrc, setDisplaySrc] = useState(avatarSrc);

    useEffect(() => {
        setDisplaySrc(avatarSrc || null);
    }, [avatarSrc]);

    const goldBorder = side === "self"
        ? accepted
            ? colors.primary.main
            : alpha(colors.primary.main, 0.42)
        : accepted
          ? alpha(colors.primary.main, 0.55)
          : "rgba(255,255,255,0.12)";
    const mockAvatar = member?.mockAvatar;

    return (
        <Box
            sx={{
                width: { xs: "2.7rem", xl: "3.05rem" },
                height: { xs: "2.7rem", xl: "3.05rem" },
                borderRadius: "50%",
                overflow: "hidden",
                flexShrink: 0,
                boxSizing: "border-box",
                border: `1.5px solid ${goldBorder}`,
                background: mockAvatar
                    ? mockAvatar
                    : side === "enemy"
                      ? "#1D1D21"
                      : `
                            radial-gradient(
                                circle at top,
                                rgba(246,185,76,0.16),
                                rgba(255,255,255,0.03)
                            )
                        `,
                boxShadow: `
                    inset 0 1px 0 rgba(255,255,255,0.06),
                    0 6px 14px rgba(0,0,0,0.35)
                `,
                [MOBILE_NAV_MQ]: {
                    width: "2.35rem",
                    height: "2.35rem",
                },
                [SHORT_VIEWPORT_MQ]: {
                    width: "2.1rem",
                    height: "2.1rem",
                },
            }}
        >
            {isKnown && displaySrc ? (
                <Box
                    component="img"
                    src={displaySrc}
                    alt=""
                    onError={() => {
                        if (displaySrc !== member?.picture && member?.picture) {
                            setDisplaySrc(member.picture);
                            return;
                        }
                        setDisplaySrc(null);
                    }}
                    sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                    }}
                />
            ) : (
                <Box
                    aria-hidden
                    sx={{
                        width: "100%",
                        height: "100%",
                        display: "grid",
                        placeItems: "center",
                    }}
                >
                    {mockAvatar ? null : (
                        <PersonRounded
                            sx={{
                                fontSize: { xs: "1.25rem", xl: "1.4rem" },
                                color: alpha("#fff", 0.32),
                                [MOBILE_NAV_MQ]: {
                                    fontSize: "1.1rem",
                                },
                            }}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
}

PlayerAvatar.propTypes = {
    slot: PropTypes.shape({
        kind: PropTypes.oneOf(["self", "party", "unknown"]),
        accepted: PropTypes.bool,
        member: PropTypes.object,
    }),
    avatarCacheBust: PropTypes.number,
    edgeBasePath: PropTypes.string,
    side: PropTypes.oneOf(["self", "enemy"]).isRequired,
};

function StatusPip({ accepted, side }) {
    return (
        <Box
            aria-hidden
            sx={{
                width: "0.5rem",
                height: "0.5rem",
                borderRadius: "50%",
                flexShrink: 0,
                boxSizing: "border-box",
                background: accepted ? colors.accent.main : "transparent",
                border: accepted
                    ? "none"
                    : `1.5px solid ${
                          side === "self"
                              ? alpha(colors.primary.main, 0.45)
                              : "rgba(255,255,255,0.22)"
                      }`,
                boxShadow: accepted
                    ? `0 0 6px ${alpha(colors.accent.main, 0.55)}`
                    : "none",
                transition:
                    "background 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
            }}
        />
    );
}

StatusPip.propTypes = {
    accepted: PropTypes.bool,
    side: PropTypes.oneOf(["self", "enemy"]).isRequired,
};

function TeamPlayerRow({ slot, side, avatarCacheBust, edgeBasePath }) {
    const accepted = Boolean(slot?.accepted);
    const isYou = slot?.kind === "self";
    const isUnknown = slot?.kind === "unknown";

    return (
        <Box
            aria-label={slotAriaLabel(slot)}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: "0.7rem", xl: "0.8rem" },
                minHeight: { xs: "3.15rem", xl: "3.45rem" },
                pl: side === "self"
                    ? { xs: "0.95rem", xl: "1.1rem" }
                    : { xs: "1.25rem", xl: "1.5rem" },
                pr: side === "self"
                    ? { xs: "1.25rem", xl: "1.5rem" }
                    : { xs: "0.95rem", xl: "1.1rem" },
                position: "relative",
                py: { xs: "0.42rem", xl: "0.48rem" },
                boxSizing: "border-box",
                flexDirection: side === "enemy" ? "row-reverse" : "row",
                borderBottom: accepted
                    ? "1px solid transparent"
                    : "1px solid rgba(255,255,255,0.06)",
                background: accepted
                    ? `
                        linear-gradient(
                            ${side === "enemy" ? "270deg" : "90deg"},
                            ${alpha(colors.primary.main, 0.12)} 0%,
                            ${alpha(colors.primary.main, 0.045)} 46%,
                            transparent 100%
                        )
                    `
                    : "transparent",
                transition: "background 0.15s ease-in-out",
                "&::after": accepted
                    ? {
                          content: '""',
                          position: "absolute",
                          inset: 0,
                          border: `1px solid ${alpha(colors.accent.main, 0.32)}`,
                          pointerEvents: "none",
                      }
                    : undefined,
                "&:last-of-type": {
                    borderBottom: "none",
                },
                [MOBILE_NAV_MQ]: {
                    flexDirection: "row",
                    minHeight: "2.85rem",
                    gap: "0.55rem",
                    px: "0.75rem",
                    py: "0.35rem",
                },
                [SHORT_VIEWPORT_MQ]: {
                    minHeight: "2.55rem",
                    py: "0.28rem",
                },
            }}
        >
            <PlayerAvatar
                slot={slot}
                avatarCacheBust={avatarCacheBust}
                edgeBasePath={edgeBasePath}
                side={side}
            />
            <Typography
                sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: { xs: "0.95rem", xl: "1.05rem" },
                    fontWeight: isYou ? 700 : 600,
                    color: isYou
                        ? colors.accent.main
                        : isUnknown
                          ? colors.text.muted
                          : colors.text.primary,
                    textAlign: side === "enemy" ? "right" : "left",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    letterSpacing: isUnknown ? "0.02em" : 0,
                    [MOBILE_NAV_MQ]: {
                        fontSize: "0.84rem",
                        textAlign: "left",
                    },
                }}
            >
                {isUnknown ? (
                    <WaitingLabel accepted={accepted} />
                ) : (
                    slotDisplayName(slot)
                )}
            </Typography>
            <StatusPip accepted={accepted} side={side} />
        </Box>
    );
}

TeamPlayerRow.propTypes = {
    slot: PropTypes.shape({
        kind: PropTypes.oneOf(["self", "party", "unknown"]),
        accepted: PropTypes.bool,
        member: PropTypes.object,
    }),
    side: PropTypes.oneOf(["self", "enemy"]).isRequired,
    avatarCacheBust: PropTypes.number,
    edgeBasePath: PropTypes.string,
};

function TeamColumn({
    side,
    slots,
    name,
    avgElo,
    avatarCacheBust,
    edgeBasePath,
}) {
    const isSelf = side === "self";
    const alignEnd = !isSelf;

    return (
        <Box
            role="group"
            aria-label={name || (isSelf ? "Your team" : "Enemy team")}
            sx={{
                width: { xs: "19.5rem", xl: "22rem" },
                maxWidth: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "0.55rem",
                [MOBILE_NAV_MQ]: {
                    width: "100%",
                    maxWidth: "22rem",
                    gap: "0.4rem",
                    ...(!isSelf ? { mt: "1.25rem" } : {}),
                },
            }}
        >
            {name || avgElo ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: alignEnd ? "flex-end" : "flex-start",
                        gap: "0.5rem",
                        textAlign: alignEnd ? "right" : "left",
                        [MOBILE_NAV_MQ]: {
                            justifyContent: "flex-start",
                            textAlign: "left",
                        },
                    }}
                >
                    {name ? (
                        <Typography
                            sx={{
                                fontFamily: DISPLAY,
                                fontSize: { xs: "0.62rem", xl: "0.66rem" },
                                fontWeight: 700,
                                letterSpacing: "0.08em",
                                color: isSelf
                                    ? colors.primary.main
                                    : colors.text.muted,
                                lineHeight: 1,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {name}
                        </Typography>
                    ) : null}
                    {avgElo ? (
                        <Typography
                            sx={{
                                fontFamily: MONO,
                                fontSize: "0.7rem",
                                fontWeight: 500,
                                color: isSelf
                                    ? colors.text.secondary
                                    : colors.text.muted,
                                lineHeight: 1,
                                flexShrink: 0,
                            }}
                        >
                            {avgElo} avg
                        </Typography>
                    ) : null}
                </Box>
            ) : null}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    background: `
                        linear-gradient(
                            165deg,
                            rgba(14,14,16,0.96) 0%,
                            rgba(10,10,12,0.98) 52%,
                            rgba(7,7,9,0.99) 100%
                        )
                    `,
                    border: "1px solid rgba(255,255,255,0.06)",
                    clipPath: isSelf
                        ? "polygon(0 0, 100% 0, 94% 100%, 0 100%)"
                        : "polygon(6% 0, 100% 0, 100% 100%, 0 100%)",
                    [MOBILE_NAV_MQ]: {
                        clipPath: "none",
                    },
                }}
            >
                {slots.map((slot, index) => (
                    <TeamPlayerRow
                        key={`${slot.kind}-${slot.userId || index}`}
                        slot={slot}
                        side={side}
                        avatarCacheBust={avatarCacheBust}
                        edgeBasePath={edgeBasePath}
                    />
                ))}
            </Box>
        </Box>
    );
}

TeamColumn.propTypes = {
    side: PropTypes.oneOf(["self", "enemy"]).isRequired,
    slots: PropTypes.arrayOf(PropTypes.object).isRequired,
    name: PropTypes.string,
    avgElo: PropTypes.string,
    avatarCacheBust: PropTypes.number,
    edgeBasePath: PropTypes.string,
};

function AcceptMatchAction({
    hasAccepted,
    allAccepted,
    onAcceptMatch,
    compact = false,
}) {
    const confirmed = hasAccepted || allAccepted;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: compact ? "0.45rem" : "0.65rem",
                width: "100%",
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: compact
                        ? "16.5rem"
                        : { xs: "18.5rem", xl: "20rem" },
                }}
            >
                <AnimatePresence mode="wait" initial={false}>
                    <MotionBox
                        key={confirmed ? "accepted-cta" : "accept-cta"}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15, ease: "easeInOut" }}
                        sx={{ width: "100%" }}
                    >
                        <LobbyMainCta
                            label={confirmed ? "Accepted" : "Accept Match"}
                            onClick={onAcceptMatch}
                            disabled={confirmed}
                            variant={confirmed ? "accepted" : "default"}
                            sx={{
                                width: "100%",
                                height: compact
                                    ? "2.55rem"
                                    : {
                                          xs: "3.05rem",
                                          xl: "3.35rem",
                                      },
                                [SHORT_VIEWPORT_MQ]: {
                                    height: compact ? "2.3rem" : "2.45rem",
                                },
                            }}
                            labelSx={{
                                fontSize: compact
                                    ? "0.74rem"
                                    : {
                                          xs: "0.88rem",
                                          xl: "0.98rem",
                                      },
                                letterSpacing: compact ? "0.1em" : "0.12em",
                                [SHORT_VIEWPORT_MQ]: {
                                    fontSize: "0.68rem",
                                },
                            }}
                        />
                    </MotionBox>
                </AnimatePresence>
            </Box>
            {compact && confirmed ? null : (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.35rem",
                        maxWidth: "26rem",
                        px: "0.35rem",
                        visibility: confirmed ? "hidden" : "visible",
                    }}
                >
                    <WarningAmberOutlined
                        aria-hidden
                        sx={{
                            fontSize: "0.78rem",
                            color: alpha(colors.primary.main, 0.45),
                            flexShrink: 0,
                        }}
                    />
                    <Typography
                        sx={{
                            fontSize: compact
                                ? "0.6rem"
                                : { xs: "0.68rem", xl: "0.72rem" },
                            lineHeight: 1.35,
                            color: colors.text.muted,
                            letterSpacing: "0.01em",
                        }}
                    >
                        {REPUTATION_WARNING}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

AcceptMatchAction.propTypes = {
    hasAccepted: PropTypes.bool,
    allAccepted: PropTypes.bool,
    onAcceptMatch: PropTypes.func,
    compact: PropTypes.bool,
};

function VsEmblem({
    acceptedCount,
    totalPlayers,
    allReady = false,
    preparingMapVote = false,
    children,
}) {
    return (
        <Box
            sx={{
                width: allReady
                    ? { xs: "12.5rem", xl: "14rem" }
                    : { xs: "9.25rem", xl: "10.5rem" },
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: allReady ? "0.28rem" : "0.45rem",
                position: "relative",
                alignSelf: "center",
                [MOBILE_NAV_MQ]: {
                    width: "100%",
                    maxWidth: "22rem",
                    py: 0,
                    gap: allReady ? "0.22rem" : "0.4rem",
                },
            }}
        >
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    width: { xs: "8.8rem", xl: "10rem" },
                    height: { xs: "8.8rem", xl: "10rem" },
                    clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
                    background: `
                        linear-gradient(
                            180deg,
                            ${alpha("#8C7638", 0.18)} 0%,
                            ${alpha("#3A2E14", 0.08)} 42%,
                            transparent 82%
                        )
                    `,
                    WebkitMaskImage:
                        "linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.35) 48%, transparent 88%)",
                    maskImage:
                        "linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.35) 48%, transparent 88%)",
                    [MOBILE_NAV_MQ]: {
                        display: "none",
                    },
                }}
            />
            <Typography
                sx={{
                    position: "relative",
                    fontFamily: DISPLAY,
                    fontWeight: 800,
                    fontSize: { xs: "2rem", xl: "2.35rem" },
                    letterSpacing: "0.04em",
                    lineHeight: 1,
                    color: colors.primary.main,
                    textShadow: `0 0 18px ${alpha(colors.primary.main, 0.22)}`,
                    [MOBILE_NAV_MQ]: {
                        fontSize: "1.15rem",
                        letterSpacing: "0.18em",
                    },
                }}
            >
                VS
            </Typography>
            {allReady ? (
                <>
                    <Typography
                        aria-label={`${acceptedCount} of ${totalPlayers} players ready`}
                        sx={{
                            position: "relative",
                            fontFamily: DISPLAY,
                            fontSize: { xs: "0.82rem", xl: "0.9rem" },
                            fontWeight: 700,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            color: colors.text.secondary,
                            fontVariantNumeric: "tabular-nums",
                            [MOBILE_NAV_MQ]: {
                                fontSize: "0.7rem",
                            },
                        }}
                    >
                        {acceptedCount}
                        <Box
                            component="span"
                            sx={{ color: colors.text.muted }}
                        >
                            {` / ${totalPlayers}`}
                        </Box>
                    </Typography>
                    <Typography
                        sx={{
                            position: "relative",
                            fontFamily: DISPLAY,
                            fontSize: { xs: "0.62rem", xl: "0.66rem" },
                            fontWeight: 700,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: colors.text.primary,
                            [MOBILE_NAV_MQ]: {
                                fontSize: "0.56rem",
                            },
                        }}
                    >
                        {MAP_VOTE_ALL_READY_LABEL}
                    </Typography>
                    {preparingMapVote ? (
                        <Typography
                            sx={{
                                position: "relative",
                                mt: { xs: "0.7rem", xl: "0.85rem" },
                                fontFamily: DISPLAY,
                                fontSize: { xs: "0.58rem", xl: "0.62rem" },
                                fontWeight: 600,
                                letterSpacing: "0.18em",
                                textTransform: "uppercase",
                                color: colors.primary.main,
                                "@keyframes matchReadyPreparingBloom": {
                                    "0%": {
                                        opacity: 0,
                                        textShadow: "none",
                                    },
                                    "42%": {
                                        opacity: 1,
                                        textShadow: `0 0 12px ${alpha(colors.primary.main, 0.28)}`,
                                    },
                                    "100%": {
                                        opacity: 1,
                                        textShadow: `0 0 6px ${alpha(colors.primary.main, 0.1)}`,
                                    },
                                },
                                animation:
                                    "matchReadyPreparingBloom 520ms ease-out 120ms both",
                                "@media (prefers-reduced-motion: reduce)": {
                                    animation: "none",
                                    opacity: 1,
                                    textShadow: "none",
                                },
                                [MOBILE_NAV_MQ]: {
                                    mt: "0.55rem",
                                    fontSize: "0.52rem",
                                },
                            }}
                        >
                            {MAP_VOTE_PREPARING_LABEL}
                        </Typography>
                    ) : null}
                </>
            ) : (
                <Typography
                    aria-label={`${acceptedCount} of ${totalPlayers} players ready`}
                    sx={{
                        position: "relative",
                        fontFamily: DISPLAY,
                        fontSize: { xs: "0.74rem", xl: "0.8rem" },
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: colors.text.muted,
                        fontVariantNumeric: "tabular-nums",
                        [MOBILE_NAV_MQ]: {
                            fontSize: "0.64rem",
                        },
                    }}
                >
                    <Box
                        component="span"
                        sx={{ color: colors.text.secondary }}
                    >
                        {acceptedCount}
                    </Box>
                    {` / ${totalPlayers} ready`}
                </Typography>
            )}
            {allReady ? null : children}
        </Box>
    );
}

VsEmblem.propTypes = {
    acceptedCount: PropTypes.number.isRequired,
    totalPlayers: PropTypes.number.isRequired,
    allReady: PropTypes.bool,
    preparingMapVote: PropTypes.bool,
    children: PropTypes.node,
};

export default function LobbyMatchReadyStatus({
    matchAcceptance,
    onAcceptMatch,
    partyMembers = [],
    selfUser = null,
    avatarCacheBust = 0,
    regionsLabel = "All Regions",
    serverLocations = [],
}) {
    const isMobileNav = useMediaQuery(MOBILE_NAV_MEDIA);
    const edgeBasePath = utils.getEdgeBasePath();
    const mapId = matchAcceptance?.map || null;
    const mapVotePending = Boolean(matchAcceptance?.mapVotePending);
    const mapMeta = getMapMetaById(mapId);
    const mapDisplayName = mapMeta?.display || mapId || "Selected map";
    const mapImageUrl = useMemo(
        () =>
            !mapVotePending && mapId
                ? getMapImageUrl(mapId, edgeBasePath)
                : null,
        [mapVotePending, mapId, edgeBasePath]
    );

    useEffect(() => {
        if (!mapImageUrl) return undefined;
        const img = new Image();
        img.src = mapImageUrl;
        return undefined;
    }, [mapImageUrl]);
    const hasAccepted = Boolean(
        matchAcceptance?.hasAccepted || matchAcceptance?.accepted
    );
    const allAccepted = Boolean(matchAcceptance?.allAccepted);
    const acceptedCount = Number(matchAcceptance?.acceptedCount) || 0;
    const totalPlayers = Number(matchAcceptance?.totalPlayers) || 2;
    const timeoutSeconds = Number(matchAcceptance?.timeoutSeconds) || 30;
    const selfUserId = selfUser?.userId || null;
    const matchTitle = allAccepted
        ? "Match Confirmed"
        : hasAccepted
          ? "Match Accepted"
          : "Match Found";
    const sideCount = Math.max(1, Math.floor(totalPlayers / 2));
    const typeLabel =
        totalPlayers % 2 === 0
            ? `${sideCount}v${sideCount}`
            : `${totalPlayers} players`;
    const teamSplitAt = Math.ceil(totalPlayers / 2);

    const regionDisplay = useMemo(() => {
        const matchedLocationId =
            matchAcceptance?.location != null
                ? String(matchAcceptance.location).trim()
                : "";
        if (matchedLocationId) {
            const locations = Array.isArray(serverLocations)
                ? serverLocations
                : [];
            const matched = locations.find((entry) => {
                const id = String(entry?.id || entry?.awsRegion || "").trim();
                return id && id === matchedLocationId;
            });
            return (
                matched?.displayName ||
                matched?.name ||
                matched?.country ||
                matchedLocationId
            );
        }
        if (typeof regionsLabel === "string" && regionsLabel.trim()) {
            return regionsLabel.trim();
        }
        return "All Regions";
    }, [matchAcceptance?.location, regionsLabel, serverLocations]);

    const orderedPartyMembers = useMemo(() => {
        const fromLobby = Array.isArray(partyMembers)
            ? partyMembers.filter((member) => member?.userId)
            : [];
        if (fromLobby.length > 0) return fromLobby;
        if (selfUser?.userId) return [selfUser];
        return [];
    }, [partyMembers, selfUser]);

    const playerSlots = useMemo(
        () =>
            buildViewerPlayerSlots({
                totalPlayers,
                partyMembers: orderedPartyMembers,
                selfUserId,
                partyAcceptedUserIds: matchAcceptance?.partyAcceptedUserIds,
                acceptedCount,
                hasAccepted,
            }),
        [
            totalPlayers,
            orderedPartyMembers,
            selfUserId,
            matchAcceptance?.partyAcceptedUserIds,
            acceptedCount,
            hasAccepted,
        ]
    );

    const yourTeam = useMemo(
        () => playerSlots.slice(0, teamSplitAt),
        [playerSlots, teamSplitAt]
    );
    const enemyTeam = useMemo(
        () => playerSlots.slice(teamSplitAt),
        [playerSlots, teamSplitAt]
    );
    const contractTeams = matchAcceptance?.teams || {};
    const yourTeamName =
        contractTeams.yours?.name ||
        formatMatchTeamName(
            yourTeam.find((slot) => slot?.kind === "self")?.member ||
                yourTeam.find((slot) => slot?.member)?.member ||
                selfUser
        );
    const enemyTeamName = contractTeams.theirs?.name || null;
    const yourTeamAvgElo = formatAvgEloValue(
        contractTeams.yours?.avgElo ?? avgEloFromSlots(yourTeam)
    );
    const enemyTeamAvgElo = formatAvgEloValue(contractTeams.theirs?.avgElo);

    const [timeLeft, setTimeLeft] = useState(() => {
        const fromExpires = remainingSecondsFromExpiresAt(
            matchAcceptance?.expiresAt
        );
        if (fromExpires != null) return fromExpires;
        return timeoutSeconds;
    });
    const [progress, setProgress] = useState(1);

    const countdownTotalRef = useRef({ matchId: null, totalMs: null });
    if (
        matchAcceptance?.matchId &&
        countdownTotalRef.current.matchId !== matchAcceptance.matchId
    ) {
        const expiresAt = Number(matchAcceptance?.expiresAt);
        const fromExpiresMs = Number.isFinite(expiresAt)
            ? Math.max(0, expiresAt - Date.now())
            : null;
        countdownTotalRef.current = {
            matchId: matchAcceptance.matchId,
            totalMs: Math.max(
                1,
                fromExpiresMs != null ? fromExpiresMs : timeoutSeconds * 1000
            ),
        };
    }

    useEffect(() => {
        const reduceMotion =
            typeof window !== "undefined" &&
            window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        const totalMs =
            countdownTotalRef.current.totalMs || timeoutSeconds * 1000;

        const tick = () => {
            const expiresAt = Number(matchAcceptance?.expiresAt);
            let remainingMs;
            if (Number.isFinite(expiresAt)) {
                remainingMs = Math.max(0, expiresAt - Date.now());
            } else {
                remainingMs = Math.max(0, timeLeft * 1000);
            }
            setTimeLeft(Math.ceil(remainingMs / 1000));
            setProgress(Math.min(1, remainingMs / totalMs));
        };

        tick();
        const id = window.setInterval(tick, reduceMotion ? 250 : 50);
        return () => window.clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchAcceptance?.expiresAt, matchAcceptance?.matchId, timeoutSeconds]);

    const isUrgent = !allAccepted && timeLeft > 0 && timeLeft <= 8;

    return (
        <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeInOut" }}
            sx={{
                width: "100%",
                minWidth: 0,
                flex: "1 1 auto",
                minHeight: { xs: "28rem", xl: "32rem" },
                mx: 0,
                my: 0,
                boxSizing: "border-box",
                pointerEvents: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                textAlign: "left",
                position: "relative",
                overflow: "hidden",
                background: `
                    radial-gradient(
                        ellipse 70% 55% at 50% 42%,
                        ${alpha(colors.primary.main, 0.055)},
                        transparent 70%
                    )
                `,
                [MOBILE_NAV_MQ]: {
                    minHeight: 0,
                },
                [SHORT_VIEWPORT_MQ]: {
                    minHeight: 0,
                },
            }}
        >
            {mapImageUrl ? (
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        backgroundImage: `url(${mapImageUrl})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center 32%",
                        backgroundRepeat: "no-repeat",
                        opacity: 0.16,
                        filter: "saturate(0.65) brightness(0.52)",
                        WebkitMaskImage:
                            "radial-gradient(ellipse 72% 58% at 50% 36%, rgba(0,0,0,0.95) 0%, transparent 74%)",
                        maskImage:
                            "radial-gradient(ellipse 72% 58% at 50% 36%, rgba(0,0,0,0.95) 0%, transparent 74%)",
                    }}
                />
            ) : null}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: "0.85rem",
                    px: { xs: "1.25rem", xl: "1.7rem" },
                    pt: { xs: "1.15rem", xl: "1.4rem" },
                    [MOBILE_NAV_MQ]: {
                        px: "0.9rem",
                        pt: "1.35rem",
                        gap: "0.55rem",
                    },
                    [SHORT_VIEWPORT_MQ]: {
                        px: "0.8rem",
                        pt: "1.1rem",
                    },
                }}
            >
                <Box
                    sx={{
                        minWidth: 0,
                        flex: 1,
                        textAlign: "left",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: { xs: "0.75rem", xl: "0.9rem" },
                    }}
                >
                    {mapImageUrl ? (
                        <Box
                            aria-hidden
                            sx={{
                                width: { xs: "5.35rem", xl: "6.2rem" },
                                height: { xs: "3rem", xl: "3.5rem" },
                                flexShrink: 0,
                                borderRadius: "7px",
                                overflow: "hidden",
                                border: "1px solid rgba(255,255,255,0.08)",
                                backgroundImage: `url(${mapImageUrl})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                                backgroundColor: "rgba(12,12,14,0.9)",
                                [MOBILE_NAV_MQ]: {
                                    width: "4.55rem",
                                    height: "2.55rem",
                                },
                                [SHORT_VIEWPORT_MQ]: {
                                    width: "4.1rem",
                                    height: "2.3rem",
                                },
                            }}
                        />
                    ) : null}
                    <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                    <Typography
                        sx={{
                            fontFamily: DISPLAY,
                            fontSize: { xs: "0.68rem", xl: "0.74rem" },
                            fontWeight: 700,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: colors.primary.main,
                            lineHeight: 1.1,
                            textAlign: "left",
                            [MOBILE_NAV_MQ]: {
                                fontSize: "0.58rem",
                            },
                        }}
                    >
                        {matchTitle}
                    </Typography>
                    <Typography
                        sx={{
                            mt: { xs: "0.28rem", xl: "0.32rem" },
                            fontSize: { xs: "1.2rem", xl: "1.4rem" },
                            fontWeight: 700,
                            letterSpacing: "0.01em",
                            color: colors.text.primary,
                            lineHeight: 1.15,
                            textAlign: "left",
                            [MOBILE_NAV_MQ]: {
                                fontSize: "1.02rem",
                            },
                            [SHORT_VIEWPORT_MQ]: {
                                fontSize: "0.95rem",
                            },
                        }}
                    >
                        {mapVotePending ? (
                            <>
                                {hasAccepted || allAccepted
                                    ? MAP_VOTE_ACCEPTED_HEADLINE
                                    : MAP_VOTE_ACCEPT_HEADLINE}{" "}
                                <Box
                                    component="span"
                                    sx={{
                                        color: colors.text.secondary,
                                        fontWeight: 500,
                                    }}
                                >
                                    {MAP_VOTE_ACCEPT_HEADLINE_SUFFIX}
                                </Box>
                            </>
                        ) : (
                            mapDisplayName
                        )}
                    </Typography>
                    </Box>
                </Box>
                <CircularCountdown
                    timeLeft={timeLeft}
                    progress={progress}
                    isUrgent={isUrgent}
                />
            </Box>

            <Box
                sx={{
                    px: { xs: "1.25rem", xl: "1.7rem" },
                    pt: { xs: "0.75rem", xl: "0.9rem" },
                    [MOBILE_NAV_MQ]: {
                        px: "0.9rem",
                        pt: "0.55rem",
                    },
                    [SHORT_VIEWPORT_MQ]: {
                        px: "0.8rem",
                        pt: "0.4rem",
                    },
                }}
            >
                <MatchMetaRow
                    modeLabel="Competitive"
                    typeLabel={typeLabel}
                    regionLabel={regionDisplay}
                />
            </Box>

            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: { xs: "2rem", xl: "2.75rem" },
                    px: { xs: "1rem", xl: "1.25rem" },
                    py: { xs: "1.1rem", xl: "1.35rem" },
                    minHeight: 0,
                    [MOBILE_NAV_MQ]: {
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "0.9rem",
                        px: "0.75rem",
                        pt: "2rem",
                        pb: "0.35rem",
                    },
                    [SHORT_VIEWPORT_MQ]: {
                        pt: "1.55rem",
                        pb: "0.3rem",
                        gap: "0.7rem",
                    },
                }}
            >
                <TeamColumn
                    side="self"
                    slots={yourTeam}
                    name={yourTeamName}
                    avgElo={yourTeamAvgElo}
                    avatarCacheBust={avatarCacheBust}
                    edgeBasePath={edgeBasePath}
                />
                <VsEmblem
                    acceptedCount={acceptedCount}
                    totalPlayers={totalPlayers}
                    allReady={allAccepted}
                    preparingMapVote={allAccepted && mapVotePending}
                >
                    {isMobileNav ? (
                        <Box sx={{ width: "100%", mt: "0.15rem" }}>
                            <AcceptMatchAction
                                hasAccepted={hasAccepted}
                                allAccepted={allAccepted}
                                onAcceptMatch={onAcceptMatch}
                                compact
                            />
                        </Box>
                    ) : null}
                </VsEmblem>
                <TeamColumn
                    side="enemy"
                    slots={enemyTeam}
                    name={enemyTeamName}
                    avgElo={enemyTeamAvgElo}
                    avatarCacheBust={avatarCacheBust}
                    edgeBasePath={edgeBasePath}
                />
            </Box>

            {isMobileNav || allAccepted ? null : (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        px: { xs: "1.25rem", xl: "1.7rem" },
                        pb: { xs: "1.25rem", xl: "1.5rem" },
                    }}
                >
                    <AcceptMatchAction
                        hasAccepted={hasAccepted}
                        allAccepted={allAccepted}
                        onAcceptMatch={onAcceptMatch}
                    />
                </Box>
            )}
        </MotionBox>
    );
}

LobbyMatchReadyStatus.propTypes = {
    matchAcceptance: PropTypes.shape({
        matchId: PropTypes.string,
        map: PropTypes.string,
        mapVotePending: PropTypes.bool,
        location: PropTypes.string,
        expiresAt: PropTypes.number,
        timeoutSeconds: PropTypes.number,
        hasAccepted: PropTypes.bool,
        accepted: PropTypes.bool,
        allAccepted: PropTypes.bool,
        acceptedCount: PropTypes.number,
        totalPlayers: PropTypes.number,
        partyAcceptedUserIds: PropTypes.arrayOf(PropTypes.string),
        slots: PropTypes.shape({
            team1: PropTypes.array,
            team2: PropTypes.array,
        }),
        teams: PropTypes.shape({
            yours: PropTypes.shape({
                name: PropTypes.string,
                avgElo: PropTypes.number,
            }),
            theirs: PropTypes.shape({
                name: PropTypes.string,
                avgElo: PropTypes.number,
            }),
        }),
    }),
    onAcceptMatch: PropTypes.func.isRequired,
    partyMembers: PropTypes.arrayOf(PropTypes.object),
    selfUser: PropTypes.object,
    avatarCacheBust: PropTypes.number,
    regionsLabel: PropTypes.string,
    serverLocations: PropTypes.arrayOf(PropTypes.object),
};
